import { JSX } from 'react';
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Guid from 'common/values/guid/guid'
import { AccessLevel, Resource, ResourceType, Scope } from './general'
import Forum from "messaging/entities/forum/forum";

export interface ICommunicationsAccessResource {
  rawText?: string;
  accessLevel?: string;
  scope?: string;
  isDisabled?: boolean;
}
export interface IForumAccessResource {
  rawText?: string;
  forumId?: string;
  accessLevel?: string;
  isDisabled?: boolean;
}
export enum DocumentSubjectMatter {
  'Financial',
  'Legal',
  'Confidential',
}

export class CommunicationsAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.CommunicationsAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `communications ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Communications</strong>
      <span>{`: ${this.accessLevel} (${this.scope})`}</span></>} />;
  }

  public override toString(): string {
    return `Communications: ${this.accessLevel} (${this.scope})`;
  }

  public fromObject(obj: ICommunicationsAccessResource): CommunicationsAccess {
    const resource = new CommunicationsAccess();
    resource.rawText = obj.rawText || "";
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): CommunicationsAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(CommunicationsAccess, fieldIterations) as CommunicationsAccess[];
  }
}


export class ForumAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.ForumAccess;
  public forumId?: Guid;
  public accessLevel?: AccessLevel;
  public rawText?: string;

  public get metaText(): string {
    return `forum ${this.forumId?.toString()} ${this.accessLevel}`;
  }

  public asJSXElement = (options: {
    forum?: Forum;
    isLoadingForum: boolean | undefined;
    errorLoadingForum: string | null | undefined;
  }): JSX.Element => {
    if (options?.isLoadingForum) {
      return <Chip label={<>
        <strong>Forum</strong>
        <span>{`: Loading...`}</span></>}
      />;
    }

    if (options?.errorLoadingForum) {
      return <>
        <Chip label={<>
          <strong>Forum</strong>
          <span>
            {`: ${options?.forum?.name || this.forumId?.toString()} (${this.accessLevel})`}
          </span>
        </>} />
        <small style={{ color: 'red', display: 'flex', alignItems: 'center' }}>
          <Icon color="error">error</Icon>
          {options?.errorLoadingForum}
        </small>
      </>;
    }

    return <Chip label={<>
      <strong>Forum</strong>
      <span>
        {`: ${options?.forum?.name || this.forumId?.toString()} (${this.accessLevel})`}
      </span>
    </>} />;
  };

  public override toString(options: {
    forum: Forum;
    isLoadingForum: boolean | undefined;
    errorLoadingForum: string | null | undefined;
  }): string {
    if (options?.isLoadingForum) {
      return `Forum: Loading...`;
    }

    if (options?.errorLoadingForum) {
      return `Forum: ${this.forumId?.toString()} (${this.accessLevel}) - Error: ${options.errorLoadingForum}`;
    }

    return `Forum: ${options?.forum?.name || this.forumId?.toString()} (${this.accessLevel})`;
  }

  public fromObject(obj: IForumAccessResource): Resource {
    const resource = new ForumAccess();
    resource.rawText = obj.rawText || "";
    resource.forumId = obj.forumId ? new Guid(obj.forumId) : undefined;
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      documentId: this.forumId?.toString(),
      documentAccessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      isDisabled: this.isDisabled
    });
  }
}
